/**
 * PageThumbnail.jsx
 * 
 * Mini preview of a page for use in the page list.
 * Renders a simplified version of the page layout.
 */

import React from 'react';
import {
  BookOpen,
  List,
  FileText,
  Image as ImageIcon,
  Star,
  Users,
  Heart
} from 'lucide-react';

export default function PageThumbnail({ page, mini = false }) {
  const baseClass = mini 
    ? 'w-full h-full flex items-center justify-center bg-gray-50' 
    : 'w-full h-full';
  
  // Special page types
  switch (page.type) {
    case 'cover':
      return (
        <div className={`${baseClass} bg-gradient-to-b from-ocean-50 to-ocean-100`}>
          <BookOpen className="w-4 h-4 text-ocean-400" />
        </div>
      );
      
    case 'toc':
      return (
        <div className={`${baseClass} bg-white flex-col gap-0.5 p-1`}>
          <div className="w-full h-0.5 bg-gray-200 rounded" />
          <div className="w-3/4 h-0.5 bg-gray-200 rounded" />
          <div className="w-full h-0.5 bg-gray-200 rounded" />
        </div>
      );
      
    case 'section':
      return (
        <div className={`${baseClass} bg-gradient-to-br from-ocean-50 to-white`}>
          <span className="text-[6px] text-ocean-400 font-medium truncate px-0.5">
            {page.sectionTitle?.substring(0, 8) || 'Section'}
          </span>
        </div>
      );
      
    case 'family-tree':
      return (
        <div className={`${baseClass} bg-white`}>
          <Users className="w-4 h-4 text-gray-300" />
        </div>
      );
      
    case 'birth-story':
      return (
        <div className={`${baseClass} bg-white`}>
          <Heart className="w-4 h-4 text-pink-300" />
        </div>
      );
      
    case 'milestone':
      return (
        <div className={`${baseClass} bg-ocean-50`}>
          <Star className="w-4 h-4 text-ocean-400" />
        </div>
      );
      
    case 'blank':
      return (
        <div className={`${baseClass} bg-white border border-gray-100`} />
      );
      
    default:
      // Content page - show layout preview
      return <LayoutThumbnail layout={page.layout} />;
  }
}

function LayoutThumbnail({ layout }) {
  const photoClass = 'bg-gray-300 rounded-[1px]';
  const textClass = 'bg-gray-200 rounded-[1px]';
  
  switch (layout) {
    case 'hero':
    case 'full-bleed':
      return (
        <div className="w-full h-full">
          <div className={`w-full h-full ${photoClass}`} />
        </div>
      );
      
    case 'hero-text':
      return (
        <div className="w-full h-full flex flex-col">
          <div className={`w-full h-3/4 ${photoClass}`} />
          <div className="w-full h-1/4 bg-white p-0.5 flex flex-col gap-px">
            <div className={`w-full h-0.5 ${textClass}`} />
            <div className={`w-2/3 h-0.5 ${textClass}`} />
          </div>
        </div>
      );
      
    case 'grid-2':
      return (
        <div className="w-full h-full flex gap-px p-px">
          <div className={`w-1/2 h-full ${photoClass}`} />
          <div className={`w-1/2 h-full ${photoClass}`} />
        </div>
      );
      
    case 'grid-3':
      return (
        <div className="w-full h-full flex gap-px p-px">
          <div className={`w-1/2 h-full ${photoClass}`} />
          <div className="w-1/2 h-full flex flex-col gap-px">
            <div className={`w-full h-1/2 ${photoClass}`} />
            <div className={`w-full h-1/2 ${photoClass}`} />
          </div>
        </div>
      );
      
    case 'grid-4':
      return (
        <div className="w-full h-full grid grid-cols-2 grid-rows-2 gap-px p-px">
          <div className={photoClass} />
          <div className={photoClass} />
          <div className={photoClass} />
          <div className={photoClass} />
        </div>
      );
      
    case 'text-left':
      return (
        <div className="w-full h-full flex">
          <div className="w-1/2 h-full bg-white p-0.5 flex flex-col gap-px">
            <div className={`w-full h-0.5 ${textClass}`} />
            <div className={`w-3/4 h-0.5 ${textClass}`} />
            <div className={`w-full h-0.5 ${textClass}`} />
          </div>
          <div className={`w-1/2 h-full ${photoClass}`} />
        </div>
      );
      
    case 'text-right':
      return (
        <div className="w-full h-full flex">
          <div className={`w-1/2 h-full ${photoClass}`} />
          <div className="w-1/2 h-full bg-white p-0.5 flex flex-col gap-px">
            <div className={`w-full h-0.5 ${textClass}`} />
            <div className={`w-3/4 h-0.5 ${textClass}`} />
            <div className={`w-full h-0.5 ${textClass}`} />
          </div>
        </div>
      );
      
    case 'text-only':
      return (
        <div className="w-full h-full bg-white p-1 flex flex-col gap-0.5">
          <div className={`w-full h-0.5 ${textClass}`} />
          <div className={`w-3/4 h-0.5 ${textClass}`} />
          <div className={`w-full h-0.5 ${textClass}`} />
          <div className={`w-2/3 h-0.5 ${textClass}`} />
        </div>
      );
      
    default:
      return (
        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
          <ImageIcon className="w-3 h-3 text-gray-300" />
        </div>
      );
  }
}
